/**
 * Validated Signal Form
 * 
 * Form for creating and editing signals with full AJV validation
 */

import {
    Alert,
    Badge,
    Button,
    Card,
    Group,
    JsonInput,
    Paper,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title
} from '@mantine/core';
import {
    IconCheck,
    IconAlertTriangle,
    IconInfoCircle,
    IconX
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';
import { useSignalMutations } from '../data';
import type { Signal } from '../data/types/WorkflowEntities';

interface SignalData {
    instanceId: string;
    signalName: string;
    payload: any;
    metadata?: {
        source: string;
        timestamp: string;
        correlation?: string;
        priority: 'low' | 'normal' | 'high' | 'urgent';
    };
}

interface ValidatedSignalFormProps {
    initialData?: Partial<SignalData>;
    mode: 'create' | 'edit';
    onSuccess?: (data: SignalData) => void;
    onCancel?: () => void;
}

export const ValidatedSignalForm: React.FC<ValidatedSignalFormProps> = ({
    initialData,
    mode,
    onSuccess,
    onCancel
}) => {
    const { sendSignal, updateSignal } = useSignalMutations();
    const [showMetadata, setShowMetadata] = useState(!!initialData?.metadata);

    // Initialize validated form
    const form = useValidatedForm<Partial<SignalData>>({
        schemaName: 'Signal',
        initialValues: {
            instanceId: initialData?.instanceId || '',
            signalName: initialData?.signalName || '',
            payload: initialData?.payload || {},
            metadata: initialData?.metadata || {
                source: '',
                timestamp: new Date().toISOString(),
                priority: 'normal'
            }
        },
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false
    });

    // Form submission with GraphQL
    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'Signal',
        () => ({
            mutate: async (data: Partial<SignalData>) => {
                if (mode === 'create') {
                    return await sendSignal(data as any);
                } else {
                    return await updateSignal(initialData?.instanceId || '', data as unknown as Partial<Signal>);
                }
            },
            loading: false,
            error: null
        })
    );

    // Signal name options (predefined common signals)
    const signalOptions = [
        'user.action.complete',
        'system.timeout',
        'payment.received',
        'approval.granted',
        'approval.denied',
        'error.occurred',
        'process.escalated',
        'schedule.triggered'
    ];

    // Priority options
    const priorityOptions = [
        { value: 'low', label: 'Low Priority' },
        { value: 'normal', label: 'Normal Priority' },
        { value: 'high', label: 'High Priority' },
        { value: 'urgent', label: 'Urgent Priority' }
    ];

    // Handle form submission
    const handleSubmit = async (values: Partial<SignalData>) => {
        const result = await submitForm(values);
        if (result.success && onSuccess) {
            onSuccess(result.data);
        }
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSubmit(values as Partial<SignalData>))}>
            <Stack gap="lg">
                {/* Header */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Title order={3}>
                                {mode === 'create' ? 'Send' : 'Edit'} Signal
                            </Title>
                            <Text size="sm" c="dimmed">
                                Signal data is validated using JSON Schema
                            </Text>
                        </div>
                        <Group>
                            <Badge 
                                color={form.validationResult.valid ? 'green' : 'red'}
                                leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconX size={14} />}
                            >
                                {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} Error${form.validationResult.errors.length > 1 ? 's' : ''}`}
                            </Badge>
                        </Group>
                    </Group>
                </Paper>

                {/* Validation Summary */}
                {form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}>
                        <Stack gap="xs">
                            {form.validationResult.errors.map((error, index) => (
                                <Text key={index} size="sm">
                                    <strong>{error.field}:</strong> {error.message}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {form.validationResult.warnings && form.validationResult.warnings.length > 0 && (
                    <Alert color="yellow" title="Validation Warnings" icon={<IconInfoCircle />}>
                        <Stack gap="xs">
                            {form.validationResult.warnings.map((warning, index) => (
                                <Text key={index} size="sm">
                                    <strong>{warning.field}:</strong> {warning.message}
                                    {warning.suggestion && <Text c="dimmed"> • {warning.suggestion}</Text>}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Basic Signal Information */}
                <Card withBorder>
                    <Title order={4} mb="md">Signal Information</Title>
                    <Stack gap="md">
                        <TextInput
                            label="Instance ID"
                            placeholder="workflow-instance-123"
                            required
                            {...form.getInputProps('instanceId')}
                            error={form.getFieldError('instanceId')}
                            rightSection={
                                form.isFieldValid('instanceId') && form.values.instanceId ? 
                                <IconCheck size={16} color="green" /> : undefined
                            }
                        />

                        <Select
                            label="Signal Name"
                            placeholder="Select or enter signal name"
                            data={signalOptions}
                            searchable
                            required
                            {...form.getInputProps('signalName')}
                            error={form.getFieldError('signalName')}
                        />

                        <JsonInput
                            label="Signal Payload"
                            placeholder='{ "key": "value" }'
                            validationError="Invalid JSON"
                            formatOnBlur
                            autosize
                            minRows={3}
                            maxRows={10}
                            {...form.getInputProps('payload')}
                            error={form.getFieldError('payload')}
                            value={typeof form.values.payload === 'string' ? form.values.payload : JSON.stringify(form.values.payload || {}, null, 2)}
                            onChange={(value) => {
                                try {
                                    const parsed = JSON.parse(value || '{}');
                                    form.setFieldValue('payload', parsed);
                                } catch {
                                    form.setFieldValue('payload', value);
                                }
                            }}
                        />
                    </Stack>
                </Card>

                {/* Metadata */}
                <Card withBorder>
                    <Group justify="space-between" mb="md">
                        <Title order={4}>Signal Metadata</Title>
                        <Button
                            variant="light"
                            size="xs"
                            onClick={() => setShowMetadata(!showMetadata)}
                        >
                            {showMetadata ? 'Hide' : 'Show'} Metadata
                        </Button>
                    </Group>

                    {showMetadata && (
                        <Stack gap="md">
                            <TextInput
                                label="Source System"
                                placeholder="e.g., payment-service, user-interface"
                                {...form.getInputProps('metadata.source')}
                                error={form.getFieldError('metadata.source')}
                            />

                            <TextInput
                                label="Timestamp"
                                placeholder="ISO 8601 timestamp"
                                {...form.getInputProps('metadata.timestamp')}
                                error={form.getFieldError('metadata.timestamp')}
                            />

                            <Group grow>
                                <TextInput
                                    label="Correlation ID (Optional)"
                                    placeholder="correlation-123"
                                    {...form.getInputProps('metadata.correlation')}
                                    error={form.getFieldError('metadata.correlation')}
                                />

                                <Select
                                    label="Priority"
                                    data={priorityOptions}
                                    {...form.getInputProps('metadata.priority')}
                                    error={form.getFieldError('metadata.priority')}
                                />
                            </Group>
                        </Stack>
                    )}
                </Card>

                {/* Form Actions */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="sm" c="dimmed">
                                Signal will be validated before sending
                            </Text>
                            {form.isValidating && (
                                <Text size="sm" c="blue">
                                    Validating...
                                </Text>
                            )}
                        </div>
                        
                        <Group>
                            <Button variant="light" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={submitting || form.isValidating}
                                disabled={!form.validationResult.valid}
                            >
                                {mode === 'create' ? 'Send Signal' : 'Update Signal'}
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Submission Result */}
                {submissionResult && (
                    <Alert 
                        color={submissionResult.success ? 'green' : 'red'}
                        title={submissionResult.success ? 'Success' : 'Error'}
                    >
                        {submissionResult.success ? 
                            'Signal sent successfully!' : 
                            submissionResult.error
                        }
                    </Alert>
                )}
            </Stack>
        </form>
    );
};

export default ValidatedSignalForm;